import React, { useState, useEffect } from 'react';
import { 
  getNotificationLogs, getNotificationTemplates, getNotificationSettings,
  testNotification, sendExpiryReminders, getClients, sendNotification
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  MessageCircle, Mail, Phone, Send, Settings, History, 
  FileText, AlertCircle, CheckCircle, Clock, XCircle,
  Bell, Zap, Users, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

export default function NotificationCenterPage() {
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  
  // Send notification dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedChannels, setSelectedChannels] = useState(['whatsapp', 'email']);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [sending, setSending] = useState(false);
  
  // Bulk reminder dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDays, setBulkDays] = useState(30);
  const [bulkSending, setBulkSending] = useState(false);
  
  // Filters
  const [filterChannel, setFilterChannel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    loadData();
  }, [filterChannel, filterStatus]);

  const loadData = async () => {
    try {
      const [logsRes, templatesRes, settingsRes, clientsRes] = await Promise.all([
        getNotificationLogs({ channel: filterChannel || undefined, status: filterStatus || undefined, limit: 100 }),
        getNotificationTemplates(),
        getNotificationSettings(),
        getClients({ limit: 500 })
      ]);
      setLogs(logsRes.data.items);
      setTemplates(templatesRes.data);
      setSettings(settingsRes.data);
      setClients(clientsRes.data.items);
    } catch (error) {
      toast.error('Failed to load notification data');
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async (channel) => {
    try {
      const result = await testNotification(channel, 'WELCOME');
      if (result.data.success) {
        toast.success(`Test ${channel} sent successfully (${settings?.mode} mode)`);
      } else {
        toast.error(`Test failed: ${result.data.error}`);
      }
      loadData();
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const handleSendNotification = async () => {
    if (!selectedClient || !selectedTemplate) {
      toast.error('Please select client and template');
      return;
    }
    
    setSending(true);
    try {
      // Get client for variables
      const client = clients.find(c => c.id === selectedClient);
      
      // Build variables based on template
      const variables = {
        client_name: client?.display_name || 'Customer',
        client_code: client?.client_code || 'N/A',
        document_type: 'Visa',
        worker_name: 'Sample Worker',
        document_number: 'V-123456',
        expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        days_remaining: '30'
      };
      
      const result = await sendNotification({
        client_id: selectedClient,
        template_name: selectedTemplate,
        variables,
        channels: selectedChannels,
        language: selectedLanguage
      });
      
      if (result.data.success) {
        toast.success('Notification sent successfully');
        setSendDialogOpen(false);
        loadData();
      } else {
        toast.error('Some notifications failed to send');
      }
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleSendBulkReminders = async () => {
    setBulkSending(true);
    try {
      const result = await sendExpiryReminders({
        days_threshold: bulkDays,
        channels: selectedChannels,
        language: selectedLanguage
      });
      
      toast.success(`Sent: ${result.data.sent}, Failed: ${result.data.failed}, Skipped: ${result.data.skipped}`);
      setBulkDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to send bulk reminders');
    } finally {
      setBulkSending(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>;
      case 'mock_sent':
        return <Badge className="bg-blue-100 text-blue-700"><Zap className="w-3 h-3 mr-1" />Mock</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp':
        return <MessageCircle className="w-4 h-4 text-green-600" />;
      case 'sms':
        return <Phone className="w-4 h-4 text-blue-600" />;
      case 'email':
        return <Mail className="w-4 h-4 text-purple-600" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" />
            Notification Center
          </h1>
          <p className="text-muted-foreground">
            Manage WhatsApp, SMS, and Email notifications
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkDialogOpen(true)}>
            <Calendar className="w-4 h-4 mr-2" />
            Send Expiry Reminders
          </Button>
          <Button onClick={() => setSendDialogOpen(true)}>
            <Send className="w-4 h-4 mr-2" />
            Send Notification
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Mode</p>
                <p className="text-2xl font-bold capitalize">{settings?.mode || 'Mock'}</p>
              </div>
              <Settings className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">WhatsApp</p>
                <p className="text-lg font-medium">
                  {settings?.whatsapp_configured ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Ready
                    </span>
                  ) : (
                    <span className="text-yellow-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> Not Configured
                    </span>
                  )}
                </p>
              </div>
              <MessageCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">SMS</p>
                <p className="text-lg font-medium">
                  {settings?.sms_configured ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Ready
                    </span>
                  ) : (
                    <span className="text-yellow-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> Not Configured
                    </span>
                  )}
                </p>
              </div>
              <Phone className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="text-lg font-medium">
                  {settings?.email_configured ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Ready
                    </span>
                  ) : (
                    <span className="text-yellow-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> Not Configured
                    </span>
                  )}
                </p>
              </div>
              <Mail className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="logs" className="w-full">
        <TabsList>
          <TabsTrigger value="logs" className="gap-2">
            <History className="w-4 h-4" /> Delivery Logs
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="w-4 h-4" /> Templates
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" /> Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Notification Logs</CardTitle>
                <div className="flex gap-2">
                  <Select value={filterChannel || 'all'} onValueChange={(v) => setFilterChannel(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="All Channels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Channels</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="mock_sent">Mock</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No notification logs yet</p>
                  <p className="text-sm">Send a test notification to see logs here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent At</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs">{log.notification_number}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getChannelIcon(log.channel)}
                            <span className="capitalize">{log.channel}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{log.recipient}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.template_name}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(log.created_at)}</TableCell>
                        <TableCell className="text-red-600 text-sm max-w-[200px] truncate">
                          {log.error || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Templates</CardTitle>
              <CardDescription>
                Pre-defined bilingual templates (English & Arabic)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <Card key={template.name} className="border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center justify-between">
                        {template.name.replace(/_/g, ' ')}
                        <div className="flex gap-1">
                          {template.channels.includes('whatsapp') && (
                            <MessageCircle className="w-4 h-4 text-green-600" />
                          )}
                          {template.channels.includes('sms') && (
                            <Phone className="w-4 h-4 text-blue-600" />
                          )}
                          {template.channels.includes('email_subject') && (
                            <Mail className="w-4 h-4 text-purple-600" />
                          )}
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      <p className="text-muted-foreground mb-2">
                        Languages: {template.languages.join(', ').toUpperCase()}
                      </p>
                      {template.sample_en.sms && (
                        <div className="bg-muted/50 p-2 rounded text-xs">
                          <strong>SMS:</strong> {template.sample_en.sms}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Test Notifications</CardTitle>
                <CardDescription>
                  Send test messages to verify configuration (Mode: {settings?.mode})
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-medium">WhatsApp</p>
                      <p className="text-sm text-muted-foreground">
                        Test: {settings?.test_recipients?.whatsapp}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleTestNotification('whatsapp')}
                  >
                    Send Test
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Phone className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-medium">SMS</p>
                      <p className="text-sm text-muted-foreground">
                        Test: {settings?.test_recipients?.sms}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleTestNotification('sms')}
                  >
                    Send Test
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-6 h-6 text-purple-600" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-sm text-muted-foreground">
                        Test: {settings?.test_recipients?.email}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleTestNotification('email')}
                  >
                    Send Test
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Configuration Status</CardTitle>
                <CardDescription>
                  Current notification service configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Twilio Account</span>
                    {settings?.twilio_configured ? (
                      <Badge className="bg-green-100 text-green-700">Configured</Badge>
                    ) : (
                      <Badge variant="secondary">Not Set</Badge>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">WhatsApp Number</span>
                    {settings?.whatsapp_configured ? (
                      <Badge className="bg-green-100 text-green-700">Configured</Badge>
                    ) : (
                      <Badge variant="secondary">Not Set</Badge>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">SMS Number</span>
                    {settings?.sms_configured ? (
                      <Badge className="bg-green-100 text-green-700">Configured</Badge>
                    ) : (
                      <Badge variant="secondary">Not Set</Badge>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Email (SMTP)</span>
                    {settings?.email_configured ? (
                      <Badge className="bg-green-100 text-green-700">Configured</Badge>
                    ) : (
                      <Badge variant="secondary">Not Set</Badge>
                    )}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    <strong>Note:</strong> In <code className="bg-muted px-1 rounded">{settings?.mode}</code> mode, 
                    notifications are logged but not actually sent. Set <code className="bg-muted px-1 rounded">NOTIFICATION_MODE=production</code> in 
                    environment to enable real sending.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Send Notification Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Notification</DialogTitle>
            <DialogDescription>
              Send a notification to a client via selected channels
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.name} value={t.name}>
                      {t.name.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Language</Label>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">Arabic (العربية)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Channels</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={selectedChannels.includes('whatsapp')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedChannels([...selectedChannels, 'whatsapp']);
                      } else {
                        setSelectedChannels(selectedChannels.filter(c => c !== 'whatsapp'));
                      }
                    }}
                    className="rounded"
                  />
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  WhatsApp
                </label>
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={selectedChannels.includes('sms')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedChannels([...selectedChannels, 'sms']);
                      } else {
                        setSelectedChannels(selectedChannels.filter(c => c !== 'sms'));
                      }
                    }}
                    className="rounded"
                  />
                  <Phone className="w-4 h-4 text-blue-600" />
                  SMS
                </label>
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={selectedChannels.includes('email')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedChannels([...selectedChannels, 'email']);
                      } else {
                        setSelectedChannels(selectedChannels.filter(c => c !== 'email'));
                      }
                    }}
                    className="rounded"
                  />
                  <Mail className="w-4 h-4 text-purple-600" />
                  Email
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendNotification} disabled={sending}>
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Expiry Reminders Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Expiry Reminders</DialogTitle>
            <DialogDescription>
              Send reminders to all clients with documents expiring within the specified days
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Days Threshold</Label>
              <Input 
                type="number" 
                value={bulkDays} 
                onChange={(e) => setBulkDays(parseInt(e.target.value) || 30)}
                min={1}
                max={90}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Send reminders for documents expiring within {bulkDays} days
              </p>
            </div>
            
            <div>
              <Label>Language</Label>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">Arabic (العربية)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Channels</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={selectedChannels.includes('whatsapp')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedChannels([...selectedChannels, 'whatsapp']);
                      } else {
                        setSelectedChannels(selectedChannels.filter(c => c !== 'whatsapp'));
                      }
                    }}
                    className="rounded"
                  />
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  WhatsApp
                </label>
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={selectedChannels.includes('email')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedChannels([...selectedChannels, 'email']);
                      } else {
                        setSelectedChannels(selectedChannels.filter(c => c !== 'email'));
                      }
                    }}
                    className="rounded"
                  />
                  <Mail className="w-4 h-4 text-purple-600" />
                  Email
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendBulkReminders} disabled={bulkSending}>
              {bulkSending ? 'Sending...' : 'Send Reminders'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
